import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { Button } from '@spark/ui-components/components/Button';
import { NotaTms, descreverAmbienteTms } from './ProgramacaoHavanMotoristaForaTmsModal';

export const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

// Renavam: 9 a 11 dígitos (os antigos ganham zeros à esquerda) com dígito verificador. Mesma regra do backend.
export const normalizarRenavam = valor => {
  const digitos = String(valor || '').replace(/\D/g, '');
  if (digitos.length < 9 || digitos.length > 11) return null;
  const renavam = digitos.padStart(11, '0');
  if (/^(\d)\1{10}$/.test(renavam)) return null;
  const pesos = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const soma = pesos.reduce((total, peso, indice) => total + Number(renavam[indice]) * peso, 0);
  const digito = (soma * 10) % 11 === 10 ? 0 : (soma * 10) % 11;
  return digito === Number(renavam[10]) ? renavam : null;
};

/**
 * Cadastro no TMS de uma placa fora do TMS. O TMS exige o mínimo: placa, tipo (cavalo/carreta, pela célula),
 * se é terceiro ou próprio, UF e Renavam.
 */
const ProgramacaoHavanVeiculoTmsModal = ({ show, campo, placa, cadastroTms, onClose, onSubmit }) => {
  const [uf, setUf] = useState('');
  const [renavam, setRenavam] = useState('');
  const [terceiro, setTerceiro] = useState(true);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const cavalo = campo === 'placa_cavalo';

  useEffect(() => {
    if (!show) return;
    setUf('');
    setRenavam('');
    setTerceiro(true);
    setTentouSalvar(false);
  }, [show]);

  const erroUf = UFS.includes(uf) ? '' : 'Informe a UF do veículo.';
  const renavamNormalizado = normalizarRenavam(renavam);
  const erroRenavam = !renavam.trim() ? 'Informe o Renavam.' : !renavamNormalizado ? 'Renavam inválido. Confira os dígitos (9 a 11 números).' : '';

  const handleSubmit = event => {
    event.preventDefault();
    setTentouSalvar(true);
    if (erroUf || erroRenavam) return;
    onSubmit?.({ uf, renavam: renavamNormalizado, terceiro });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>{cavalo ? 'Cadastrar cavalo no TMS' : 'Cadastrar carreta no TMS'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Resumo>
            <span>Placa</span>
            <strong>{placa}</strong>
            <span>Tipo</span>
            <strong>{cavalo ? 'Cavalo (tração)' : 'Carreta (reboque)'}</strong>
          </Resumo>
          <Linha>
            <Form.Group controlId="veiculoTmsUf">
              <Form.Label>UF do veículo</Form.Label>
              <Form.Select value={uf} isInvalid={tentouSalvar && Boolean(erroUf)} onChange={event => setUf(event.target.value)}>
                <option value="">Selecione</option>
                {UFS.map(sigla => <option key={sigla} value={sigla}>{sigla}</option>)}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{erroUf}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="veiculoTmsRenavam">
              <Form.Label>Renavam</Form.Label>
              <Form.Control value={renavam} inputMode="numeric" maxLength={14} placeholder="00000000000" isInvalid={tentouSalvar && Boolean(erroRenavam)} onChange={event => setRenavam(event.target.value.replace(/[^\d]/g, ''))} />
              <Form.Control.Feedback type="invalid">{erroRenavam}</Form.Control.Feedback>
            </Form.Group>
          </Linha>
          <Propriedade role="radiogroup" aria-label="Propriedade do veículo">
            <Form.Check inline id="veiculoTmsTerceiro" type="radio" name="veiculoTmsPropriedade" label="Terceiro" checked={terceiro} onChange={() => setTerceiro(true)} />
            <Form.Check inline id="veiculoTmsProprio" type="radio" name="veiculoTmsPropriedade" label="Próprio" checked={!terceiro} onChange={() => setTerceiro(false)} />
          </Propriedade>
          <NotaTms $homologacao={cadastroTms?.ambiente !== 'producao'}>
            Ambiente do TMS: <strong>{descreverAmbienteTms(cadastroTms?.ambiente)}</strong>.
            {' '}O cadastro é o mínimo que o TMS exige; os demais dados do veículo podem ser completados no TMS depois.
          </NotaTms>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary">Cadastrar no TMS</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

ProgramacaoHavanVeiculoTmsModal.propTypes = {
  show: PropTypes.bool,
  campo: PropTypes.string,
  placa: PropTypes.string,
  cadastroTms: PropTypes.shape({ habilitado: PropTypes.bool, ambiente: PropTypes.string }),
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

ProgramacaoHavanVeiculoTmsModal.defaultProps = {
  show: false,
  campo: '',
  placa: '',
  cadastroTms: { habilitado: false, ambiente: '' },
  onClose: undefined,
  onSubmit: undefined
};

export default ProgramacaoHavanVeiculoTmsModal;

const Resumo = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #d7e0ea;
  border-radius: 4px;
  background: #f8fafc;
  font-size: 13px;
  span { color: #526275; }
`;
const Linha = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;
const Propriedade = styled.div`margin-bottom: 10px; font-size: 13px;`;
